import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Gift, LogOut, Mail, QrCode, Star, Ticket } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, Reveal } from "@/components/shared";

const GC_STATUS_LABELS = {
  active: "Active",
  partielle: "Partiellement utilisée",
  utilisee: "Entièrement utilisée",
  expiree: "Expirée",
  annulee: "Annulée",
  remboursee: "Remboursée",
  suspendue: "Suspendue",
};

const GC_STATUS_COLORS = {
  active: "border-gold/40 text-gold",
  partielle: "border-copper/50 text-copper",
  utilisee: "border-white/20 text-ivory/50",
  expiree: "border-red-400/40 text-red-400",
  annulee: "border-white/20 text-ivory/40",
  remboursee: "border-white/20 text-ivory/40",
  suspendue: "border-red-400/40 text-red-400",
};

const fmt = (n) => Number(n).toFixed(2).replace(".", ",") + " €";
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function MonEspace() {
  const [email, setEmail] = useState(null);
  const [step, setStep] = useState("email"); // email | code
  const [form, setForm] = useState({ email: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [cards, setCards] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [converting, setConverting] = useState(false);

  const load = () => {
    api.get("/espace/mes-cartes").then((r) => setCards(r.data)).catch(() => {});
    api.get("/espace/fidelite").then((r) => setLoyalty(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/espace/mes-cartes")
      .then(() => {
        setEmail("ok");
        load();
      })
      .catch(() => setEmail(null));
  }, []);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/espace/request-code", { email: form.email });
      setStep("code");
      toast.success("Code envoyé par email (valable 15 min).");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/espace/verify", form);
      setEmail(form.email);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post("/espace/fidelite/convertir");
      toast.success(`Bon de ${fmt(data.value)} créé : ${data.code}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setConverting(false);
    }
  };

  const logout = async () => {
    await api.post("/espace/logout").catch(() => {});
    setEmail(null);
    setStep("email");
    setForm({ email: "", code: "" });
  };

  if (!email) {
    return (
      <div data-testid="espace-page">
        <PageHero overline="Mon espace" title="Vos cartes & votre fidélité" testId="espace-hero">
          <p>Entrez votre email : nous vous envoyons un code de connexion à usage unique.</p>
        </PageHero>
        <section className="px-6 pb-28 md:px-12">
          <div className="mx-auto max-w-md">
            <Reveal>
              <form onSubmit={step === "email" ? requestCode : verify} className="space-y-4 rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="espace-login-form">
                {step === "email" ? (
                  <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Votre email" className="input-dark" data-testid="espace-email-input" />
                ) : (
                  <>
                    <p className="text-sm text-ivory/60">Code envoyé à <strong className="text-ivory">{form.email}</strong></p>
                    <input required inputMode="numeric" maxLength={6} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "") }))} placeholder="Code à 6 chiffres" className="input-dark text-center text-2xl tracking-[0.5em]" data-testid="espace-code-input" />
                  </>
                )}
                <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="espace-submit-button">
                  <Mail className="h-4 w-4" strokeWidth={1.5} />
                  {busy ? "Patientez…" : step === "email" ? "Recevoir mon code" : "Me connecter"}
                </button>
                {step === "code" && (
                  <button type="button" onClick={() => setStep("email")} className="w-full text-center text-xs text-ivory/40 hover:text-gold" data-testid="espace-back-button">
                    Renvoyer un code / changer d'email
                  </button>
                )}
              </form>
            </Reveal>
          </div>
        </section>
      </div>
    );
  }

  const points = loyalty?.points || 0;
  const threshold = loyalty?.threshold || 200;
  const progress = Math.min(100, Math.round(((points % threshold) / threshold) * 100));

  return (
    <div data-testid="espace-dashboard">
      <PageHero overline="Mon espace" title="Vos cartes & votre fidélité" testId="espace-hero" />
      <section className="px-6 pb-28 md:px-12">
        <div className="mx-auto max-w-4xl space-y-10">
          <div className="flex justify-end">
            <button onClick={logout} className="flex items-center gap-2 text-xs text-ivory/40 hover:text-gold" data-testid="espace-logout">
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              Se déconnecter
            </button>
          </div>

          <Reveal>
            <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="espace-fidelite">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                <Star className="h-4 w-4 text-gold" strokeWidth={1.5} />
                Ma fidélité
              </h2>
              {loyalty && (
                <div className="mt-6 grid gap-8 md:grid-cols-[1fr_auto]">
                  <div>
                    <p className="text-4xl font-semibold text-gold" data-testid="loyalty-points">{points} <span className="text-base text-ivory/50">points</span></p>
                    <p className="mt-2 text-sm text-ivory/60" data-testid="loyalty-progress-text">
                      {points % threshold} points sur {threshold} — encore {threshold - (points % threshold)} points pour obtenir {fmt(loyalty.reward_value)} de récompense.
                    </p>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10" data-testid="loyalty-progress-bar">
                      <div className="h-full rounded-full bg-gradient-to-r from-copper to-gold transition-all duration-700" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="mt-3 text-xs text-ivory/40">
                      {loyalty.points_per_euro} € dépensé = {loyalty.points_per_euro} point(s). Les points sont crédités après chaque paiement confirmé.
                    </p>
                    {loyalty.convertible && (
                      <button onClick={convert} disabled={converting} className="btn-primary mt-5 !h-10 !px-5 !text-[11px]" data-testid="loyalty-convert-button">
                        <Ticket className="h-4 w-4" strokeWidth={1.5} />
                        {converting ? "Conversion…" : `Convertir ${threshold} points en bon de ${fmt(loyalty.reward_value)}`}
                      </button>
                    )}
                    {loyalty.rewards.length > 0 && (
                      <div className="mt-6 space-y-2" data-testid="loyalty-rewards">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">Mes récompenses</p>
                        {loyalty.rewards.map((r) => (
                          <div key={r.id} className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3" data-testid={`reward-${r.id}`}>
                            <span className="font-mono text-sm tracking-widest text-gold">{r.code}</span>
                            <span className="text-sm text-ivory/70">{fmt(r.value)}</span>
                            <span className={`text-[11px] font-semibold uppercase ${r.status === "disponible" ? "text-gold" : "text-ivory/35"}`}>
                              {r.status === "disponible" ? "Disponible" : "Utilisée"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {loyalty.history.length > 0 && (
                      <div className="mt-6" data-testid="loyalty-history">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-ivory/50">Historique</p>
                        <div className="max-h-48 space-y-1 overflow-y-auto">
                          {loyalty.history.map((h) => (
                            <div key={h.id} className="flex items-center justify-between text-xs">
                              <span className="text-ivory/50">{h.source} · {fmtDate(h.at)}</span>
                              <span className={h.points >= 0 ? "text-gold" : "text-red-400"}>{h.points >= 0 ? "+" : ""}{h.points} pts</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="mb-2 text-xs uppercase tracking-[0.2em] text-ivory/40">Ma carte fidélité</p>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/espace/fidelite/qr.png`} alt="QR code fidélité" className="h-36 w-36 rounded-lg border border-gold/30 bg-white p-2" data-testid="loyalty-qr" />
                    {loyalty.card_number && (
                      <p className="mt-2 font-mono text-sm font-semibold tracking-[0.25em] text-gold" data-testid="loyalty-card-number">{loyalty.card_number}</p>
                    )}
                    <p className="mt-2 text-[11px] text-ivory/35">À présenter au restaurant</p>
                  </div>
                </div>
              )}
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-lg border border-white/10 bg-coal/60 p-8" data-testid="espace-cartes">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                <Gift className="h-4 w-4 text-gold" strokeWidth={1.5} />
                Mes cartes cadeaux
              </h2>
              {cards.length === 0 ? (
                <p className="mt-4 text-sm text-ivory/40" data-testid="espace-cartes-empty">Aucune carte cadeau associée à cet email pour le moment.</p>
              ) : (
                <div className="mt-6 space-y-3">
                  {cards.map((c) => (
                    <div key={c.id} className="rounded-md border border-white/10 p-5" data-testid={`espace-card-${c.id}`}>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <span className="font-mono text-sm tracking-widest text-gold">{c.code}</span>
                        <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${GC_STATUS_COLORS[c.status] || ""}`}>
                          {GC_STATUS_LABELS[c.status] || c.status}
                        </span>
                        <span className="text-[11px] uppercase tracking-wider text-ivory/35">
                          {c.role === "acheteur" ? `Offerte à ${c.recipient_name || "—"}` : `Offerte par ${c.buyer_name}`}
                        </span>
                        <a href={c.pdf_url} className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-copper hover:text-gold" data-testid={`espace-pdf-${c.id}`}>
                          <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                          PDF
                        </a>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-sm text-ivory/60">
                        <span>Montant : <strong className="text-ivory">{fmt(c.amount)}</strong></span>
                        <span>Solde : <strong className="text-gold">{fmt(c.balance ?? c.amount)}</strong></span>
                        <span>Expire le : <strong className="text-ivory">{c.expires_fr}</strong></span>
                      </div>
                      {(c.uses || []).length > 0 && (
                        <div className="mt-3 border-t border-white/10 pt-3">
                          {c.uses.filter((u) => u.amount > 0).map((u) => (
                            <p key={u.id} className="text-xs text-ivory/40">
                              {fmtDate(u.at)} — {fmt(u.amount)} utilisés (solde : {fmt(u.balance_after)})
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
